import { useEffect, useState } from "react";
import api from "../services/api";
import { message } from "antd";
import { useProcessosContext } from "./processosContext";
import { useNavigate } from "react-router-dom";
import { Titular } from "../@types/titular";

export interface Questionario {
  _id?: string;
  cliente_id?: string;
  nome?: string;
  areas?: string[];
  inativo?: boolean;
  versoes?: any;
  qtd_respondentes?: any;
  capitulos?: any;
  perguntasRelacao?: any;
  abordagem: "detalhada" | "simplificada" | undefined | null;
  created_at: string;
  updated_at: string;
  areas_descricao?: {
    area_id: string;
    cliente_id: string;
    descricao?: string;
    created_at: string;
    updated_at: string;
  }[];
}

export type MapeamentoContextType = {
  sendQuestionarios: (v: any, clienteID: string) => Promise<any>;
  setQuestionarios: (v: any) => void;
  getQuestionarios: () => void;
  getQuestionario: (quest_id: string, user_id: string) => void;
  getQuestionarioRespondente: (
    tokenUrl: string,
    loginToken: string
  ) => Promise<any>;
  getTemplates: () => void;
  postImportTemplate: (json: any) => Promise<any>;
  saveQuestionario: (
    client_id: string | null | undefined,
    questionarios: any
  ) => Promise<any>;
  questionarios: Questionario[];
  templates: any[];
  importedTemplate: any | null;
  setCurrentTitulares: (titular: Titular[] | any) => void;
  currentTitulares: Titular[] | null;
  updateQuestionarioAreas: (
    questionarioId: string,
    areas: string[],
    clienteId: string
  ) => void;
  saveDados: (
    departamento: string,
    categoria: string,
    nome: string,
    description: string
  ) => void;
  getDados: () => void;
  dados: any;
  getTitulares: (cliente_id?: string) => void;
  getFicha: () => void;
  saveTitulares: (titulares: any[] | null, cliente_id?: string) => Promise<any>;
  getQuestionariosCliente: (cliente_id: string) => Promise<any>;
  ficha: any;
  currentUserHasGestorPermission: boolean;
  setCurrentUserHasGestorPermission: (permission: boolean) => void;
  currentUserHasLiderPermission: boolean;
  setCurrentUserHasLiderPermission: (permission: boolean) => void;
  activeMapeamentoTab: string;
  setActiveMapeamentoTab: (tab: string) => void;
  currentMapeamento: Questionario | null | undefined;
  setCurrentMapeamento: (q: Questionario | null | undefined) => void;
  getAnaliseRiscoTitular: (titularId: string) => Promise<any>;
  fetchPontuacoes: () => Promise<any>;
  handleNotaChange: (id: string, nota: number) => Promise<any>;
};

export const useMapeamentoContext = (): MapeamentoContextType => {
  const navigate = useNavigate();
  const processosContext = useProcessosContext();

  const [questionarios, setQuestionarios] = useState<any[]>([]);
  const [templates, setTemplates] = useState<any[]>([]);
  const [importedTemplate, setImportedTemplate] = useState<any | null>(null);
  const [dados, setDados] = useState<any | null>(null);
  const [currentTitulares, setCurrentTitulares] = useState<Titular[] | any>(
    null
  );
  const [ficha, setFicha] = useState<Titular[] | any>(null);
  const [currentUserHasGestorPermission, setCurrentUserHasGestorPermission] =
    useState<boolean>(false);
  const [currentUserHasLiderPermission, setCurrentUserHasLiderPermission] =
    useState<boolean>(false);
  const [activeMapeamentoTab, setActiveMapeamentoTab] = useState<string>("1");
  const [currentMapeamento, setCurrentMapeamento] = useState<
    Questionario | null | undefined
  >(null);

  useEffect(() => {
    if (!currentMapeamento && questionarios?.[0]) {
      const mapeamentos = questionarios?.filter(
        (item) => item.nome === "Assessment Process"
      );

      if (mapeamentos && mapeamentos.length === 1) {
        const firstMapeamento = mapeamentos[0];

        firstMapeamento && setCurrentMapeamento(firstMapeamento);
      }
    }
  }, [questionarios]);

  // Função para obter todos os dados
  const getDados = () => {
    api
      .get("/dados")
      .then((response) => {
        setDados(response.data);
      })
      .catch((err) => {
        console.error("Erro ao buscar dados:", err);
      });
  };

  // Função para obter ficha de processo (mapeamento/questionario)
  const getFicha = () => {
    api
      .get("/ficha")
      .then((response) => {
        setFicha(response.data);
      })
      .catch((err) => {
        console.error("Erro ao buscar dados:", err);
      });
  };

  // Função para salvar um novo dado
  const saveDados = (novoDado: any) => {
    api
      .post("/dados", novoDado)
      .then(() => {
        message.success("Novo dado cadastrado com sucesso!");
        getDados(); // Atualiza a lista de dados após o cadastro
      })
      .catch((err) => {
        message.error("Erro ao cadastrar novo dado.");
        console.error("Erro ao cadastrar novo dado:", err);
      });
  };

  // Função para salvar titulares
  const getTitulares = (cliente_id?: string) => {
    if (cliente_id) {
      api
        .get("/titulares/" + cliente_id)
        .then((res) => {
          setCurrentTitulares(res.data);
        })
        .catch((err) => {});
    } else {
      api
        .get("/titulares", { params: { cliente_id } })
        .then((res) => {
          setCurrentTitulares(res.data);
        })
        .catch((err) => {});
    }
  };

  // Função para salvar titulares
  const saveTitulares = (
    titulares: any[] | null,
    cliente_id?: string
  ): Promise<string> => {
    return new Promise((resolve, reject) => {
      api
        .post("/titulares", titulares === null ? currentTitulares : titulares)
        .then(() => {
          message.success("Titulares salvos com sucesso!");
          getTitulares(cliente_id); // Atualiza a lista de titulares
          resolve("success"); // Retorna o status de sucesso
        })
        .catch((err) => {
          message.error("Erro ao salvar titulares.");
          reject("error"); // Retorna o status de erro
        });
    });
  };

  const sendQuestionarios = (v: any, clienteID: string) => {
    return new Promise<any>(async (resolve, reject) => {
      await api
        .post("sendquestionarios", {
          versao_id: v._id,
        })
        .then((response) => {
          getQuestionariosCliente(clienteID);
          if (!response.data?.route?.includes("responder")) {
            message.success(
              "Questionário enviado para respondentes com sucesso!"
            );
          }
          resolve(response.data);
        })
        .catch((err) => {
          message.error(
            "Falha ao enviar questionário para um ou mais respondente(s)!"
          );
          reject(err);
        });
    });
  };

  const getQuestionarios = () => {
    api
      .get("questionarios", {})
      .then((response) => {
        setQuestionarios(response.data);
      })
      .catch((err) => {
        console.error("ops! ocorreu um erro" + err);
      });
  };

  const getQuestionariosCliente = (cliente_id: string) => {
    return new Promise<any>(async (resolve, reject) => {
      await api
        .get("questionarios/" + cliente_id)
        .then((response) => {
          setQuestionarios(response.data);
          resolve(response.data);
        })
        .catch((err) => {
          reject(err);
        });
    });
  };

  const getQuestionarioRespondente = (tokenUrl: string, loginToken: string) => {
    return new Promise<any>(async (resolve, reject) => {
      await api
        .get(`questionariorespondente/${tokenUrl}`, {
          headers: {
            Authorization: `Bearer ${loginToken}`,
          },
        })
        .then((response) => {
          resolve(response.data);
        })
        .catch((err) => {
          reject(err);
        });
    });
  };

  const getTemplates = () => {
    api
      .get("templates", {})
      .then((response) => {
        setTemplates(response.data);
      })
      .catch((err) => {
        console.error("ops! ocorreu um erro" + err);
      });
  };

  const postImportTemplate = (json: any) => {
    return new Promise<any>(async (resolve, reject) => {
      await api
        .post("importtemplate", json)
        .then((response) => {
          setImportedTemplate(response.data);
          message.success("Área salva com sucesso!");
          resolve(response);
          processosContext?.getAreas();
          processosContext?.getCapituloPerguntas();
          processosContext?.getPerguntas();
          processosContext?.getCapitulos();
        })
        .catch((err) => {
          message.error("Erro ao salvar área importada!");
          reject(err);
        });
    });
  };

  const getQuestionario = (quest_id: string, user_id: string) => {
    api
      .get("questionariorespondente", {
        params: {
          questionario_id: quest_id,
          user_id: user_id,
        },
      })
      .then((response) => {
        setQuestionarios(response.data);
      })
      .catch((err) => {
        console.error("ops! ocorreu um erro" + err);
      });
  };

  const saveQuestionario = (
    client_id: string | null | undefined,
    questionarios: any
  ) => {
    return new Promise((resolve, reject) => {
      api
        .post("questionarios", {
          cliente_id: client_id,
          questionarios: questionarios,
        })
        .then(async () => {
          let response;
          if (client_id) {
            response = await getQuestionariosCliente(client_id);
          }
          message.success("Mapeamento salvo com sucesso!");
          if (client_id === null) {
            getTemplates();
            navigate(-1);
          }
          resolve(response); // Resolve a Promise em caso de sucesso
        })
        .catch((err) => {
          message.error("Falha ao salvar mapeamento!");
          reject(err); // Rejeita a Promise em caso de erro
        });
    });
  };

  const updateQuestionarioAreas = (
    questionarioId: string,
    areas: string[],
    clienteId: string
  ) => {
    api
      .put(`questionario/${questionarioId}`, { areas })
      .then((response) => {
        message.success("Áreas do questionário atualizadas com sucesso!");
        getQuestionariosCliente(clienteId);
      })
      .catch((err) => {
        message.error("Erro ao atualizar as áreas do questionário!");
        console.error(err);
      });
  };

  const getAnaliseRiscoTitular = (titularId: string) => {
    return new Promise(async (resolve, reject) => {
      try {
        const response = await api.get(`/riscos/titular/${titularId}`);
        resolve(response.data);
      } catch (error) {
        message.error("Erro ao buscar análise de risco do titular");
        reject(error);
      }
    });
  };

  const fetchPontuacoes = async () => {
    return new Promise((resolve, reject) => {
      api
        .get("/pontuacoes")
        .then((res) => {
          message.success("Pontuações atualizadas com sucesso!");
          resolve(res.data); // Retorna o status de sucesso
        })
        .catch((err) => {
          message.error("Erro ao salvar atualizações.");
          reject("error"); // Retorna o status de erro
        });
    });
  };

  const handleNotaChange = async (id: string, nota: number) => {
    return new Promise((resolve, reject) => {
      api
        .put(`/pontuacoes/${id}`, { nota })
        .then((res) => {
          resolve(res.data); // Retorna o status de sucesso
          message.success("Pontuações atualizadas com sucesso!");
        })
        .catch((err) => {
          message.error("Erro ao salvar atualizações.");
          reject("error"); // Retorna o status de erro
        });
    });
  };

  return {
    fetchPontuacoes,
    handleNotaChange,
    sendQuestionarios,
    getQuestionario,
    getQuestionarios,
    setQuestionarios,
    getQuestionarioRespondente,
    getTemplates,
    saveQuestionario,
    postImportTemplate,
    questionarios,
    templates,
    importedTemplate,
    setCurrentTitulares,
    currentTitulares,
    updateQuestionarioAreas,
    saveDados,
    getDados,
    dados,
    saveTitulares,
    getTitulares,
    getFicha,
    ficha,
    currentUserHasGestorPermission,
    setCurrentUserHasGestorPermission,
    activeMapeamentoTab,
    setActiveMapeamentoTab,
    currentUserHasLiderPermission,
    setCurrentUserHasLiderPermission,
    getQuestionariosCliente,
    setCurrentMapeamento,
    currentMapeamento,
    getAnaliseRiscoTitular,
  };
};
